// Sign-in screen. There's no design HTML for this — it's the portal entry
// point. We reuse the design's .search-card / .field / .btn classes so the
// visual treatment matches the rest of the app.

import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useBodyClass } from "../lib/useBodyClass";

interface LocationState { from?: string }

export function SignIn() {
  useBodyClass("has-bg2");
  const { signIn, isAuthenticated, isLoading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = (loc.state as LocationState | null)?.from || "/";

  const [identifier, setId] = useState("");
  const [password, setPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isLoading && isAuthenticated) return <Navigate to={from} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(identifier.trim(), password);
      nav(from, { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Network error. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="container" style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
      <form onSubmit={onSubmit} className="search-card" style={{ width: "min(420px, 100%)", padding: 28 }}>
        <a href="/" className="logo" style={{ marginBottom: 20 }}>
          <div className="logo-mark">K</div>
          <div className="logo-word">
            <span className="brand">the tarzan way</span>
            <span className="tag">by Kaira · est. 2018</span>
          </div>
        </a>

        <h1 className="pax-h" style={{ fontSize: 28 }}>
          Sign <span className="serif">in</span>.
        </h1>
        <p className="pax-sub" style={{ marginBottom: 20 }}>
          Use the credentials your admin shared with you.
        </p>

        <div className="form-row">
          <div className="field">
            <label>Email or username <span className="req">●</span></label>
            <input
              type="text"
              autoComplete="username"
              required
              value={identifier}
              onChange={(e) => setId(e.target.value)}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="field">
            <label>Password <span className="req">●</span></label>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPw(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div className="info-banner warn" style={{ margin: "0 0 14px" }}>
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          className="btn btn-yellow btn-pill btn-full"
          disabled={submitting}
        >
          {submitting ? <><span className="spinner" />Signing in…</> : "Sign in"}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
        </button>

        <p className="field-help" style={{ marginTop: 16, textAlign: "center" }}>
          Don't have an account? Ask your admin to bind a user to your partner row in Django admin.
        </p>
      </form>
    </div>
  );
}
